// src/LessonAnalyzePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import SharedLayout from "./SharedLayout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiChevronLeft, FiCheck } from "react-icons/fi";
import axios from "axios";
import format from "date-fns/format";
import InfoModal from "./modals/InfoModals";
import { tr } from "date-fns/locale"; // Import Turkish locale

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Güvenli tarih biçimleyici
const formattedDate = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "dd MMMM yyyy 'saat' HH:mm", { locale: tr });
};

const LessonAnalyzePage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { state } = useLocation();

  // Learners sayfasından gelen state’ler (yoksa boş)
  const learnerFromState = state?.learner ?? {};
  const enrollmentFromState = state?.course ?? {}; // Learners’da state.course = enrollment
  const courseObj = enrollmentFromState?.course ?? {}; // gerçek kurs nesnesi
  const lessonsFromCourse = Array.isArray(courseObj.lessons)
    ? courseObj.lessons
    : [];

  const lessonProgressFromState = Array.isArray(state?.lessonProgress)
    ? state.lessonProgress
    : [];
  const courseProgressFromState = state?.courseProgress ?? {};

  // Kurs adı (önce gerçek kurs adı, yoksa slug -> yumuşat)
  const courseName =
    courseObj?.name ||
    String(slug || "")
      .replace(/-/g, " ")
      .toUpperCase();

  // Ders bazlı analytics (güvenli eşleştirme)
  const initialLessons = lessonsFromCourse.map((lesson) => {
    const progress = lessonProgressFromState.find(
      (p) => p.lessonId === lesson.id
    );
    const isCompleted = !!progress?.isCompleted;
    const completedTime = progress?.completedAt
      ? formattedDate(progress.completedAt)
      : "Devam ediyor...";
    return {
      title: lesson?.title || "",
      completed: isCompleted,
      completedTime,
    };
  });

  // Üst başlık bilgileri
  const [header, setHeader] = useState({
    addedAt: formattedDate(courseObj?.createdAt),
    courseCompletedTime: courseProgressFromState?.completedAt
      ? formattedDate(courseProgressFromState.completedAt)
      : null,
    quizScore: courseProgressFromState?.quizScore ?? null,
    active: learnerFromState?.active ?? true,
    learnerName: `${learnerFromState?.name || "Ad"} ${
      learnerFromState?.surname || "Soyad"
    }`,
  });

  // Dersler
  const [lessons, setLessons] = useState(initialLessons);

  // InfoModal (hata/uyarı)
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const openInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  useEffect(() => {
    if (!courseObj?.id && lessonsFromCourse.length === 0) {
      openInfo(
        "warning",
        "Eksik Bilgi",
        "Gerekli kurs verileri bulunamadı. Lütfen Kişi Detay sayfasından bir kurs seçerek tekrar deneyin."
      );
    }
  }, []); // eslint-disable-line

  // (Opsiyonel) Ek veri fetch — örnek kanca (endpoint senin backendine göre)
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // Örnek: state’le gelinmediyse ve gerekli kimlikler mevcutsa fetch yapabilirsin
        // if (!courseObj?.id && BACKEND_URL && learnerFromState?.id && slug) {
        //   const { data } = await axios.get(
        //     `${BACKEND_URL}/learners/${learnerFromState.id}/courses/${slug}`,
        //     { headers: { "ngrok-skip-browser-warning": "true" } }
        //   );
        //   if (cancel) return;
        //   setHeader((h) => ({
        //     ...h,
        //     addedAt: formattedDate(data?.course?.createdAt),
        //     courseCompletedTime: data?.courseProgress?.completedAt
        //       ? formattedDate(data.courseProgress.completedAt)
        //       : null,
        //     quizScore: data?.courseProgress?.quizScore ?? null,
        //     active: !!data?.learner?.active,
        //     learnerName: `${data?.learner?.name || "Ad"} ${data?.learner?.surname || "Soyad"}`,
        //   }));
        //   setLessons(
        //     (data?.course?.lessons || []).map((lesson) => {
        //       const p = (data?.lessonProgress || []).find((lp) => lp.lessonId === lesson.id);
        //       return {
        //         title: lesson?.title || "",
        //         completed: !!p?.isCompleted,
        //         completedTime: p?.completedAt ? formattedDate(p.completedAt) : "Devam ediyor...",
        //       };
        //     })
        //   );
        // }
      } catch (e) {
        console.error("Lesson analyze fetch failed:", e);
        openInfo(
          "error",
          "Yükleme Hatası",
          "Kurs analiz verileri alınırken bir sorun oluştu. Lütfen daha sonra tekrar deneyin."
        );
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug, learnerFromState?.id]); // eslint-disable-line

  // Tamamlanma yüzdesi
  const completedCount = useMemo(
    () => lessons.filter((l) => l.completed).length,
    [lessons]
  );
  const totalCount = lessons.length || 0;
  const completionPct = totalCount
    ? Math.round((completedCount / totalCount) * 100)
    : 0;
  const dashOffset = 100 - Math.max(0, Math.min(completionPct, 100));

  return (
    <SharedLayout pageTitle="KATILIMCI KURS ANALİZİ">
      <div className="rounded-[24px] bg-gray-50 ring-1 ring-emerald-100 p-5 sm:p-6 lg:p-8 shadow-sm">
        <div className="mx-auto px-1 sm:px-2">
          {/* Üst şerit */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-white px-5 py-2 rounded-full shadow-sm">
                <span className="font-medium text-base sm:text-lg">
                  {header.learnerName}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 self-start sm:self-auto">
              <div className="text-lg sm:text-xl font-bold tracking-wide">
                DURUM:
              </div>
              <span
                className={`px-3 py-1 rounded-full text-base font-medium ${
                  header.active ? "text-green-600" : "text-gray-600"
                }`}
              >
                {header.active ? "AKTİF" : "PASİF"}
              </span>

              <button
                onClick={() => navigate(-1)}
                className="ml-2 inline-flex items-center justify-center h-9 w-9 rounded-full bg-green-50 text-green-600 hover:bg-green-100"
                title="Geri"
              >
                <FiChevronLeft size={42} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start mb-8 mt-8">
            {/* Sol: kurs başlığı ve küçük bilgiler */}
            <div>
              <h2 className="text-2xl sm:text-2xl font-bold tracking-tight mb-4">
                KURS:{" "}
                <span className="pl-2 text-2xl sm:text-2xl font-semibold">
                  {courseName}
                </span>
              </h2>

              <div className="bg-white rounded-2xl ring-1 ring-gray-100 p-5 sm:p-6">
                <dl className="space-y-3 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <dt className="uppercase tracking-wide text-gray-500">
                      Eklenme Tarihi:
                    </dt>
                    <dd className="mt-1 sm:mt-0 text-gray-800">
                      {header.addedAt || "—"}
                    </dd>
                  </div>
                  <div className="border-t border-gray-100 pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <dt className="uppercase tracking-wide text-gray-500">
                      Kurs Tamamlanma Süresi:
                    </dt>
                    <dd className="mt-1 sm:mt-0 text-gray-800">
                      {header.courseCompletedTime || "Devam ediyor..."}
                    </dd>
                  </div>
                  <div className="border-t border-gray-100 pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <dt className="uppercase tracking-wide text-gray-500">
                      Sınav Puanı:
                    </dt>
                    <dd className="mt-1 sm:mt-0 text-gray-800">
                      {header.courseCompletedTime
                        ? header.quizScore ?? "—"
                        : "Devam ediyor..."}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Sağ: Tamamlanma oranı */}
            <div className="bg-white rounded-xl shadow-sm p-6 overflow-hidden w-full max-w-[460px] min-h-[200px] justify-self-start md:justify-self-center">
              <h3 className="text-lg font-semibold tracking-wide text-gray-700">
                KURS TAMAMLANMA ORANI
              </h3>
              <p className="text-[11px] text-gray-500 mt-1 uppercase">
                Başarılı çalışma!
              </p>

              <div className="mt-4 flex justify-center">
                <svg
                  viewBox="0 0 36 36"
                  width="92"
                  height="92"
                  aria-label="completion"
                >
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="#e6e6e6"
                    strokeWidth="3"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="#07183c"
                    strokeWidth="3"
                    strokeDasharray="100"
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)"
                  />
                  <text
                    x="18"
                    y="20.5"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#07183c"
                    fontWeight="bold"
                  >
                    %{completionPct}
                  </text>
                </svg>
              </div>

              <div className="mt-2 text-center text-xs text-gray-500">
                {completedCount}/{totalCount}
              </div>
            </div>
          </div>

          {/* Tablo */}
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-green-600">
                    <th className="text-center text-white text-xs sm:text-sm font-semibold px-6 py-3">
                      DERSLER
                    </th>
                    <th className="text-center text-white text-xs sm:text-sm font-semibold px-6 py-3">
                      DERS TAMAMLAMA
                    </th>
                    <th className="text-center text-white text-xs sm:text-sm font-semibold px-6 py-3">
                      DERS TAMAMLAMA ZAMANI
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lessons.map((lesson, idx) => (
                    <tr key={`${lesson.title}-${idx}`} className="bg-white">
                      <td className="px-6 py-5 text-sm sm:text-base text-gray-800">
                        {lesson.title}
                      </td>
                      <td className="px-6 py-5 text-center">
                        {lesson.completed ? (
                          <span
                            className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-100 text-green-600"
                            title="Tamamlandı"
                          >
                            <FiCheck />
                          </span>
                        ) : (
                          <span className="text-transparent select-none">
                            .....
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-sm sm:text-base text-gray-700 text-center">
                        {lesson.completed
                          ? lesson.completedTime
                          : "Devam ediyor..."}
                      </td>
                    </tr>
                  ))}
                  {lessons.length === 0 && (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-10 text-center text-gray-500"
                      >
                        Ders bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Info modal */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
    </SharedLayout>
  );
};

export default LessonAnalyzePage;
